import { supabase } from './supabase.js';
import { uploadListingPhoto } from './storageService.js';
import { assertAuctionsEnabled } from './launchPhaseClient.js';

const BID_ERRORS = {
  not_authenticated: 'Inicia sesión para pujar.',
  not_auction: 'Este anuncio no es una subasta.',
  auction_not_active: 'La subasta no está activa.',
  cannot_bid_own_auction: 'No puedes pujar en tu propia subasta.',
  auction_ended: 'La subasta ha finalizado.',
  bid_too_low: 'La puja es demasiado baja.',
  use_buy_now: 'Usa «Comprar ya» para cerrar al precio indicado.',
  no_buy_now: 'Este anuncio no tiene precio de compra inmediata.',
  launch_phase_blocked: 'Las subastas están desactivadas en esta fase del lanzamiento.',
};

const SUBMIT_ERRORS = {
  not_authenticated: 'Inicia sesión para solicitar la subasta.',
  partner_required: 'Elige una tienda verificadora.',
  title_artist_required: 'Completa título y artista.',
  invalid_auction_start: 'Indica una puja inicial válida.',
  invalid_verifier_partner: 'La tienda verificadora seleccionada no está disponible.',
};

function mapBidError(error, fallback) {
  const msg = String(error?.message || '');
  for (const [code, text] of Object.entries(BID_ERRORS)) {
    if (msg.includes(code)) return text;
  }
  return fallback || msg || 'No se pudo registrar la puja.';
}

function mapSubmitError(error, fallback) {
  const msg = String(error?.message || error || '');
  for (const [code, text] of Object.entries(SUBMIT_ERRORS)) {
    if (msg.includes(code)) return text;
  }
  return fallback || msg || 'No se pudo registrar la solicitud de subasta.';
}

export async function getAuctionBidSummaries(listingIds) {
  const ids = (listingIds || []).filter(Boolean);
  if (!ids.length) return { summaries: {}, error: null };
  const { data, error } = await supabase.rpc('get_auction_bid_summaries', {
    p_listing_ids: ids,
  });
  if (error) {
    console.error('[auctionsService] getAuctionBidSummaries:', error);
    return { summaries: {}, error: error.message };
  }
  const summaries = {};
  for (const row of data ?? []) {
    summaries[row.listing_id] = {
      currentBid: row.current_bid != null ? Number(row.current_bid) : null,
      bidCount: Number(row.bid_count) || 0,
    };
  }
  return { summaries, error: null };
}

/**
 * Añade puja actual y recuento a listings de subasta (RPC + cierre si expiró).
 * @param {Array<{ id: string, isAuction?: boolean, auctionStart?: number|null, price?: number }>} listings
 */
export async function enrichListingsWithAuctionBids(listings) {
  const items = listings ?? [];
  const auctionIds = items.filter(l => l.isAuction && l.id).map(l => l.id);
  if (!auctionIds.length) return items;

  await Promise.all(auctionIds.slice(0, 30).map(id => closeAuctionIfEnded(id).catch(() => null)));

  const { summaries } = await getAuctionBidSummaries(auctionIds);
  return items.map(l => {
    if (!l.isAuction) return l;
    const s = summaries[l.id];
    if (!s) return l;
    return {
      ...l,
      currentBid: s.currentBid ?? l.auctionStart ?? l.price,
      bidCount: s.bidCount ?? 0,
    };
  });
}

export async function placeAuctionBid(listingId, amount) {
  const phaseBlock = await assertAuctionsEnabled();
  if (phaseBlock) return { bidId: null, error: phaseBlock };

  const { data, error } = await supabase.rpc('place_auction_bid', {
    p_listing_id: listingId,
    p_amount: amount,
  });
  if (error) {
    console.error('[auctionsService] placeAuctionBid:', error);
    return { bidId: null, error: mapBidError(error) };
  }
  return { bidId: data, error: null };
}

export async function auctionBuyNow(listingId) {
  const phaseBlock = await assertAuctionsEnabled();
  if (phaseBlock) return { bidId: null, error: phaseBlock };

  const { data, error } = await supabase.rpc('auction_buy_now', {
    p_listing_id: listingId,
  });
  if (error) {
    console.error('[auctionsService] auctionBuyNow:', error);
    return { bidId: null, error: mapBidError(error, 'No se pudo completar la compra inmediata.') };
  }
  return { bidId: data, error: null };
}

export async function closeAuctionIfEnded(listingId) {
  const { data, error } = await supabase.rpc('close_auction_if_ended', {
    p_listing_id: listingId,
  });
  if (error) {
    console.error('[auctionsService] closeAuctionIfEnded:', error);
    return { closed: false, error: error.message };
  }
  return { closed: !!data, error: null };
}

/**
 * Solicitud de subasta con verificación física en tienda partner.
 * @returns {Promise<{ id: string|null, error: string|null }>}
 */
export async function submitAuctionForVerification({
  partnerId,
  title,
  artist,
  format,
  condition,
  auctionStart,
  durationHours,
  auctionBuyNow = null,
  description = null,
}) {
  const phaseBlock = await assertAuctionsEnabled();
  if (phaseBlock) return { id: null, error: phaseBlock };

  const { data, error } = await supabase.rpc('submit_auction_for_verification', {
    p_partner_id: partnerId,
    p_title: title,
    p_artist: artist,
    p_format: format,
    p_condition: condition,
    p_auction_start: auctionStart,
    p_duration_hours: durationHours,
    p_auction_buy_now: auctionBuyNow,
    p_description: description,
  });
  if (error) {
    console.error('[auctionsService] submitAuctionForVerification:', error);
    return { id: null, error: mapSubmitError(error) };
  }
  return { id: data ?? null, error: null };
}

/**
 * Sube hasta 3 fotos y actualiza cover_url + photos en el listing.
 * @param {string} listingId
 * @param {string} userId
 * @param {(File|null)[]} files — índices 0..2
 */
export async function uploadAuctionListingPhotos(listingId, userId, files) {
  const urls = [];
  for (let i = 0; i < 3; i++) {
    const file = files[i];
    if (!file) continue;
    const { url, error } = await uploadListingPhoto(file, userId, listingId);
    if (error) return { error };
    if (url) urls[i] = url;
  }
  const photos = urls.filter(Boolean);
  if (!photos.length) return { error: 'Añade al menos la foto de portada.' };

  const { error: upErr } = await supabase
    .from('listings')
    .update({ cover_url: photos[0], photos })
    .eq('id', listingId)
    .eq('seller_id', userId);
  if (upErr) {
    console.error('[auctionsService] uploadAuctionListingPhotos update:', upErr);
    return { error: upErr.message };
  }
  return { error: null };
}

/** @deprecated Usar submitAuctionForVerification. Mantenido por compatibilidad interna. */
export async function createAuctionListing(row) {
  console.warn('[auctionsService] createAuctionListing está obsoleto; usa submitAuctionForVerification.');
  const { data, error } = await supabase
    .from('listings')
    .insert({
      ...row,
      is_auction: true,
      status: 'active',
    })
    .select('id')
    .single();
  if (error) {
    console.error('[auctionsService] createAuctionListing:', error);
    return { id: null, error: error.message };
  }
  return { id: data?.id ?? null, error: null };
}
